import { useCallback, useEffect, useRef, useState } from 'react'

interface State<T> {
  data: T | null
  error: string | null
  loading: boolean
}

/** Run an async loader, with a stale-response guard so a slow request that
 *  resolves after a newer one cannot overwrite it. `reload` re-runs on
 *  demand; `deps` re-runs when they change. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): State<T> & {
  reload: () => void
  set: (v: T) => void
} {
  const [state, setState] = useState<State<T>>({ data: null, error: null, loading: true })
  const gen = useRef(0)
  // Kept in a ref so `deps` alone decides when to re-run — an inline loader
  // would otherwise be a new function identity on every render.
  const fnRef = useRef(fn)
  fnRef.current = fn

  const run = useCallback(() => {
    const mine = ++gen.current
    setState((s) => ({ ...s, loading: true, error: null }))
    fnRef.current().then(
      (data) => { if (mine === gen.current) setState({ data, error: null, loading: false }) },
      (err) => {
        if (mine === gen.current) {
          setState({ data: null, error: err?.message ?? String(err), loading: false })
        }
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { run() }, deps)

  const set = useCallback((v: T) => {
    gen.current++          // any in-flight load is now stale
    setState({ data: v, error: null, loading: false })
  }, [])

  return { ...state, reload: run, set }
}
